import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format phone number for display: (203) 555-2823
 */
export function formatPhoneNumber(value: string) {
  if (!value) return value;
  let phoneNumber = value.replace(/[^\d]/g, "");
  // A pasted or autofilled "+1 (203) 505-8816" carries the US country code.
  // Keeping the FIRST ten digits turned it into "(120) 350-5881" — a number
  // that passes every length check and reaches nobody. Area codes never start
  // with 1, so an 11-digit value led by 1 is always the country code.
  if (phoneNumber.length === 11 && phoneNumber.startsWith("1")) {
    phoneNumber = phoneNumber.slice(1);
  }
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
}

/**
 * Format phone number for API (E.164 format): +12035552823
 * Payment APIs expect E.164 format with +1 country code for US numbers
 */
export function formatPhoneForAPI(phone: string): string {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Add +1 country code for US numbers (10 digits)
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // If already has country code (11 digits starting with 1), add +
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  // Return as-is if format doesn't match (let API validation handle it)
  return phone;
}

/**
 * Format price for display: $10.50
 *
 * Re-exported, not defined here. The implementation moved to
 * `@order-up/models` (`primitives/formatPrice.ts`) so that packages which need
 * four lines of `Intl` — notably `packages/board-renderer`, whose bundle is
 * embedded into the gateway binary — can have it WITHOUT pulling in this
 * package's shadcn/Radix/recharts component graph, which compiles to CommonJS
 * and does not tree-shake.
 *
 * Every existing `import { formatPrice } from '@order-up/common'` keeps working
 * and there is still exactly one implementation. The models version is
 * additionally memoised per currency; behaviour is otherwise identical.
 */
export { formatCurrency, formatPrice } from '@order-up/models';

/**
 * Format amount for API submission: "10.50"
 * Payment wire amounts are strings with exactly 2 decimal places
 * @param amount - Number to format
 * @returns String formatted as "xx.xx" (e.g., "10.50", "1100.35")
 */
export function formatAmountForAPI(amount: number): string {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0.00';
  }
  return amount.toFixed(2);
}

/**
 * Parse currency input string to number
 * Removes $, commas, and other formatting characters
 * @param value - String like "$1,234.56" or "1234.56"
 * @returns Number (e.g., 1234.56) or undefined if invalid
 */
export function parseCurrencyInput(value: string): number | undefined {
  if (!value || value.trim() === '' || value === '$') return undefined;

  // Remove $, commas, and spaces
  const cleaned = value.replace(/[$,\s]/g, '');

  // Parse to float
  const parsed = parseFloat(cleaned);

  // Return undefined if invalid, otherwise return the number
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Format number as currency string for input field
 * @param value - Number to format (e.g., 1234.56)
 * @returns Formatted string for input (e.g., "$1,234.56")
 */
export function formatCurrencyForInput(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return '';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
